import type { DocumentAsset, FileUploadResponse, UploadableType } from "@qendo/profile/files";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import type { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import { fetch } from "expo/fetch";
import { File } from "expo-file-system";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { type ImageURISource, Platform } from "react-native";
import { parse, stringify } from "superjson";
import { API_HOST } from "~/constants/environment";
import { useErrorBag } from "~/providers/ErrorBagProvider";
import { filled } from "~/util";
import { createClient, documentFile } from "~/util/api";
import { extractFilename } from "~/util/qendo/media";

export const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            gcTime: 1000 * 60 * 60 * 24, // 1 day
        },
    },
});

export const privateQueryClient = new QueryClient({
    defaultOptions: {
        queries: {
            gcTime: 1000 * 60 * 60, // 1 hour
        },
    },
});

export const asyncStoragePersister = createAsyncStoragePersister({
    storage: AsyncStorage,
    serialize: stringify,
    deserialize: parse,
});

export const privateStoragePersister = createAsyncStoragePersister({
    storage: AsyncStorage,
    serialize: stringify,
    deserialize: parse,
    key: "REACT_QUERY_PRIVATE_CACHE",
});

export type ErrorPayload<K extends object | string> = {
    message?: string;
    errors?: Partial<Record<K extends object ? keyof K : K, string[]>>;
};

export type ApiError<K extends object | string = object, T = unknown, D = unknown> = AxiosError<T & ErrorPayload<K>, D>;
export type UploadResponse = {
    file: string;
    filename: string;
};

export type FileAsset = {
    uri: string;
    fileName?: string | null;
    name?: string | null;
    mimeType?: string;
    type?: string;
} & DocumentAsset;

declare interface ApiState {
    client(): AxiosInstance;
    img(uri: string): ImageURISource;
    download(url: string, filename: string, overwrite?: boolean): Promise<File | null>;
    upload(profile_id: string, type: UploadableType, asset: FileAsset): Promise<UploadResponse>;
}

export const ApiContext = createContext<ApiState>({
    client: createClient,
    img: (uri: string): ImageURISource => ({ uri }),
    download: async (_url, _filename, _overwrite) => null,
    upload: async (_profile_id, _type, _asset) => ({
        file: "",
        filename: "",
    }),
});

export type StoreMethod = "patch" | "post";
type AxiosStoreMethod<T extends StoreMethod = StoreMethod> = {
    method: T;
    url: string;
};
export function resolveStoreMethod(url: string, id?: string | null | undefined): AxiosStoreMethod {
    return filled(id)
        ? ({ method: "patch", url: `${url}/${id}` } as AxiosStoreMethod<"patch">)
        : ({ method: "post", url } as AxiosStoreMethod<"post">);
}

export function useApi() {
    return useContext(ApiContext);
}

export type ApiProviderProps = {
    token?: string | null;
    logOut?: (soft?: boolean) => void;
    children: ReactNode;
};

function ApiProvider({ token, logOut, children }: ApiProviderProps) {
    const errorBag = useErrorBag();

    const handleUnauthorized = useCallback(
        async (error: ApiError) => {
            if (error.status === 401) {
                logOut?.(true);

                throw error;
            }

            return error;
        },
        [logOut],
    );

    const handleUnprocessableContent = useCallback(
        async (error: ApiError) => {
            if (error.status === 422) {
                if (error.response?.data?.errors) {
                    errorBag.fill(error.response.data.errors);
                }

                throw error;
            }

            return error;
        },
        [errorBag],
    );

    const handleApiError = useCallback(
        async (error: ApiError) => {
            await handleUnauthorized(error)
                .then(handleUnprocessableContent)
                .then((error) => {
                    throw error;
                });
        },
        [handleUnauthorized, handleUnprocessableContent],
    );

    const client = useCallback(() => {
        const client = createClient(handleApiError);

        if (token) {
            client.defaults.headers.common.Authorization = `Bearer ${token}`;
        }

        return client;
    }, [token, handleApiError]);

    const img = useCallback(
        (uri: string) => {
            const source = { uri };

            if (token && source.uri.startsWith(API_HOST)) {
                Object.assign(source, { headers: { Authorization: `Bearer ${token}` } });

                if (Platform.OS === "web") {
                    source.uri = `${source.uri}?_auth=${token}`;
                }
            }

            return source;
        },
        [token],
    );

    const download = useCallback(
        async (url: string, filename: string, overwrite?: boolean) => {
            const target = documentFile(filename);

            if (target.exists && !overwrite) {
                return target;
            }

            const headers = token && url.startsWith(API_HOST) ? { Authorization: `Bearer ${token}` } : undefined;
            return (
                (await File.downloadFileAsync(url, target, { headers, idempotent: overwrite }).catch(handleApiError)) ??
                null
            );
        },
        [token, handleApiError],
    );

    const [query, persister] = useMemo(
        () => (token ? [privateQueryClient, privateStoragePersister] : [queryClient, asyncStoragePersister]),
        [token],
    );

    const upload = useCallback(
        async (profile_id: string, type: UploadableType, asset: FileAsset): Promise<UploadResponse> => {
            return await client()
                .post(`profiles/${profile_id}/upload`, {
                    type,
                    content_type: asset.mimeType ?? asset.type,
                })
                .then(async (response: AxiosResponse<FileUploadResponse>) => {
                    const { url, headers, key } = response.data;

                    const body = new File(asset.uri);

                    const flat_headers = Object.fromEntries(
                        Object.entries(headers).map(([k, v]) => [k, Array.isArray(v) ? v.join("; ") : v]),
                    );

                    const uploaded = await fetch(url, {
                        method: "PUT",
                        headers: flat_headers,
                        body,
                    });

                    if (!uploaded.ok) {
                        const message = await uploaded.text();

                        throw {
                            message,
                            status: uploaded.status,
                            response: {
                                data: {
                                    message,
                                },
                            },
                        } as ApiError;
                    }

                    return {
                        file: key,
                        filename: asset.fileName ?? asset.name ?? extractFilename(asset),
                    };
                });
        },
        [client],
    );

    const value: ApiState = {
        client,
        img,
        upload,
        // @ts-expect-error
        download,
    };

    return (
        <ApiContext.Provider value={value}>
            <PersistQueryClientProvider client={query} persistOptions={{ persister }}>
                {children}
            </PersistQueryClientProvider>
        </ApiContext.Provider>
    );
}

export default ApiProvider;
